export class Context {
	/**
	 * @type {CanvasRenderingContext2D}
	 */
	ctx2d

	/**
	 * @type {HTMLCanvasElement}
	 */
	canvas

	gridStyle = {
		borderColor: "rgb(212, 212, 212)",
		borderLineWidth: 1,
		bgColor: "white",
		txtColor: "#111111",
		headerTxtColor: "#111111",
		headerBgColor: "#fafafa",
		selectBorderColor: "#fa3457",
		selectBgColor: "#f4f4ff",
		gridBgColor: "#fafafa",
		errorBgColor: "#F9B4B2"
	}

	cellHPadding = 6
	cellVPadding = 6

	currentCellInfo

	/** @type {"row"|"cell"|"multiRow"} */
	selectMode = "cell"

	get isCellSelectMode () {
		return this.selectMode === "cell"
	}

	get isRowSelectMode () {
		return this.selectMode === "row"
	}

	#gridElement

	get rowHeight () {
		return this.#gridElement.rowHeight
	}

	get headerHeight () {
		return this.#gridElement.headerHeight
	}

	get font () {
		return this.#gridElement.font
	}

	get fontSize () {
		return this.#gridElement.fontSizePt
	}

	get autoInsertRow () {
		return this.#gridElement.autoInsertRow
	}

	/**
	 * @type {boolean} 貼り付け中か
	 */
	#isPasteNow = false
	get isPasting () {
		return this.#isPasteNow
	}

	set isPasting (val) {
		this.#isPasteNow = val
	}

	/**
	 * @type {Row[]}
	 */
	rows = []

	constructor (canvas, gridElement) {
		this.canvas = canvas
		this.ctx2d = this.canvas.getContext("2d")
		this.#gridElement = gridElement
		this.selectMode = gridElement.selectMode
		const computed = window.getComputedStyle(gridElement)
		this.gridStyle.selectBorderColor = computed.getPropertyValue("--grid-select-border-color") || computed.getPropertyValue("--primary") || this.gridStyle.selectBorderColor
		this.gridStyle.bgColor = computed.getPropertyValue("--grid-bg-color") || this.gridStyle.bgColor
		this.gridStyle.txtColor = computed.getPropertyValue("--grid-color") || this.gridStyle.txtColor
		this.gridStyle.borderColor = computed.getPropertyValue("--grid-border-color") || this.gridStyle.borderColor
		this.gridStyle.selectBgColor = computed.getPropertyValue("--grid-select-bg-color") || this.gridStyle.selectBgColor
		this.gridStyle.headerTxtColor = computed.getPropertyValue("--grid-header-color") || this.gridStyle.headerTxtColor
		this.gridStyle.headerBgColor = computed.getPropertyValue("--grid-header-bg-color") || this.gridStyle.headerBgColor
		this.gridStyle.errorBgColor = computed.getPropertyValue("--error") || this.gridStyle.errorBgColor
	}

	/**
	 * @type {HTMLElement}
	 */
	get inputEl () {
		return this.#gridElement.input
	}

	get controlEl () {
		return this.#gridElement.gridControl
	}

	get currentCellInfo () {
		return this.#gridElement.currentCellInfo
	}

	get isCellEditing () {
		return this.inputEl && this.inputEl.children.length !== 0
	}

	/**
	 *
	 * @param {(ctx2d:CanvasRenderingContext2D)=>void} callback
	 */
	useContext2d (callback) {
		this.ctx2d.save()
		try {
			const d2pRatio = window.devicePixelRatio
			this.ctx2d.font = `${Math.ceil(this.fontSize * d2pRatio)}pt ${this.font}`
			callback(this.ctx2d)
		} finally {
			this.ctx2d.restore()
		}
	}

	fillRect (left, top, width, height) {
		const d2pRatio = window.devicePixelRatio
		this.ctx2d.fillRect(left * d2pRatio + 0.5, top * d2pRatio + 0.5, width * d2pRatio, height * d2pRatio)
	}

	strokeRect (left, top, width, height) {
		const d2pRatio = window.devicePixelRatio
		this.ctx2d.strokeRect(left * d2pRatio + 0.5, top * d2pRatio + 0.5, width * d2pRatio, height * d2pRatio)
	}

	strokeLine (startX, startY, endX, endY) {
		const d2pRatio = window.devicePixelRatio
		this.ctx2d.beginPath()
		this.ctx2d.moveTo(startX * d2pRatio, startY * d2pRatio)
		this.ctx2d.lineTo(endX * d2pRatio, endY * d2pRatio)
		this.ctx2d.stroke()
	}

	fillText (text, left, top, width) {
		const d2pRatio = window.devicePixelRatio
		this.ctx2d.fillText(this.fittingString(this.ctx2d, text, width * d2pRatio), left * d2pRatio + 0.5, top * d2pRatio + 0.5, width * d2pRatio)
	}

	drawImage (img, left, top, width, height) {
		const d2pRatio = window.devicePixelRatio
		this.ctx2d.drawImage(img, left * d2pRatio, top * d2pRatio, width * d2pRatio, height * d2pRatio)
	}

	fittingString (ctx, str, maxWidth) {
		let width = ctx.measureText(str).width
		const ellipsis = "…"
		const ellipsisWidth = ctx.measureText(ellipsis).width
		if (width <= maxWidth || width <= ellipsisWidth) {
			return str
		} else {
			let len = str.length
			while (width >= maxWidth - ellipsisWidth && len-- > 0) {
				str = str.substring(0, len)
				width = ctx.measureText(str).width
			}
			return str + ellipsis
		}
	}

	dispatchEvent (event) {
		this.#gridElement.dispatchEvent(event)
	}
}

export class Rect {
	#top
	#left
	#width
	#height

	constructor ({ top, left, width, height }) {
		this.#top = top
		this.#left = left
		this.#width = width
		this.#height = height
	}

	get top () {
		return this.#top
	}

	get left () {
		return this.#left
	}

	get width () {
		return this.#width
	}

	get height () {
		return this.#height
	}

	/**
	 *
	 * @param {Rect} anotherRect
	 * @returns {boolean}
	 */
	includes (anotherRect) {
		if (anotherRect.top > this.top + this.height) {
			return false
		}
		if (this.top > anotherRect.top + anotherRect.height) {
			return false
		}
		if (anotherRect.left > this.left + this.width) {
			return false
		}
		if (this.left > anotherRect.left + anotherRect.width) {
			return false
		}
		return true
	}
}
